/**
 * Helper functions for categorizing and managing filters
 * Separates date/time filters from other business logic filters
 */

import { ActiveFilter } from '../useFilters';

// Define which filters are considered "trend/date" filters
export const TREND_FILTER_NAMES = ['week', 'quarter', 'month'];

/**
 * Categorizes a filter as either 'trend' (date/time) or 'business' (other)
 */
export const categorizeFilter = (filter: ActiveFilter): 'trend' | 'business' => {
  return TREND_FILTER_NAMES.includes(filter.name || '') ? 'trend' : 'business';
};

/**
 * Separates an array of filters into trend filters and business filters
 * Also cleans empty filters automatically
 */
export const separateFilters = (filters: ActiveFilter[]): {
  trendFilters: ActiveFilter[];
  businessFilters: ActiveFilter[];
} => {
  const trendFilters: ActiveFilter[] = [];
  const businessFilters: ActiveFilter[] = [];
  
  // Clean empty filters first, then separate
  const cleanFilters = cleanEmptyFilters(filters);
  
  cleanFilters.forEach(filter => {
    if (categorizeFilter(filter) === 'trend') {
      trendFilters.push(filter);
    } else {
      businessFilters.push(filter);
    }
  });
  
  return { trendFilters, businessFilters };
};

/**
 * Combines trend filters and business filters back into a single array
 * Also cleans empty filters automatically
 */
export const combineFilters = (
  businessFilters: ActiveFilter[],
  trendFilters: ActiveFilter[]
): ActiveFilter[] => {
  const combined = [...businessFilters, ...trendFilters];
  return cleanEmptyFilters(combined);
};

const normalizeFilterSelectedForCacheKey = (selected: ActiveFilter['selected']) => {
  if (Array.isArray(selected)) {
    return [...selected].map(String).sort();
  }
  return selected;
};

export const buildFiltersConfigStructureSignature = (
  ideasFiltersConfig: { evidence?: Record<string, unknown> } | undefined
): string => {
  const evidence = ideasFiltersConfig?.evidence;
  if (!evidence || Object.keys(evidence).length === 0) {
    return '';
  }
  return Object.keys(evidence).sort().join(',');
};

const serializeFilterForCacheKey = (filter: ActiveFilter) => ({
  name: filter.name,
  label: filter.label,
  type: filter.type,
  dbFieldPath: filter.dbFieldPath,
  filterObject: filter.filterObject,
  operator: filter.operator,
  selected: normalizeFilterSelectedForCacheKey(filter.selected),
  query: filter.query,
});

const sortSerializedFilters = (
  filters: ReturnType<typeof serializeFilterForCacheKey>[]
) =>
  [...filters].sort((a, b) =>
    JSON.stringify(a).localeCompare(JSON.stringify(b))
  );

export type TabEvidenceTotalsCacheParams = {
  filters: ReturnType<typeof serializeFilterForCacheKey>[];
  trendFilters: ReturnType<typeof serializeFilterForCacheKey>[];
  sort?: { field: string; direction: 'asc' | 'desc' };
  inclusion: string;
};

export const buildTabEvidenceTotalsCacheParams = (
  activeFilters: ActiveFilter[],
  trendFilters: ActiveFilter[],
  sortConfig?: { field: string; direction: 'asc' | 'desc' },
  evidenceInclusion = 'total'
): TabEvidenceTotalsCacheParams => {
  const cleanActiveFilters = cleanEmptyFilters(activeFilters || []);
  const cleanTrendFilters = cleanEmptyFilters(trendFilters || []);

  return {
    filters: sortSerializedFilters(cleanActiveFilters.map(serializeFilterForCacheKey)),
    trendFilters: sortSerializedFilters(cleanTrendFilters.map(serializeFilterForCacheKey)),
    sort:
      sortConfig?.field && sortConfig?.direction
        ? { field: sortConfig.field, direction: sortConfig.direction }
        : undefined,
    inclusion: evidenceInclusion,
  };
};

export const buildPerspectiveFiltersCacheKey = (
  businessFilters: ActiveFilter[],
  trendFilters: ActiveFilter[]
): string => {
  const combined = combineFilters(businessFilters, trendFilters);
  if (combined.length === 0) {
    return 'no_filters';
  }

  return JSON.stringify(
    combined
      .map((filter) => ({
        name: filter.name,
        selected: normalizeFilterSelectedForCacheKey(filter.selected),
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  );
};

/**
 * Checks if a filter is a trend/date filter
 */
export const isTrendFilter = (filter: ActiveFilter): boolean => {
  return categorizeFilter(filter) === 'trend';
};

/**
 * Checks if a filter is a business logic filter
 */
export const isBusinessFilter = (filter: ActiveFilter): boolean => {
  return categorizeFilter(filter) === 'business';
};

/**
 * Checks if a filter is a change log filter
 * Change log filters have a special dbFieldPath pattern and operator pattern
 */
const isChangeLogFilter = (filter: ActiveFilter): boolean => {
  return (
    filter.dbFieldPath?.includes('.itemChangeLog[') &&
    filter.operator?.match(/^\$(gt|gte|lt|lte):.+$/) !== null
  );
};

/**
 * Checks if a filter is empty (has no meaningful selected values)
 */
export const isFilterEmpty = (filter: ActiveFilter): boolean => {
  // Change log filters are never empty - even with empty selected array,
  // they mean "match all items after the status change"
  if (isChangeLogFilter(filter)) {
    return false;
  }
  
  if (!filter.selected) return true;
  
  // For array types (checkbox, fetchableCheckbox, etc.)
  if (Array.isArray(filter.selected)) {
    return filter.selected.length === 0 || filter.selected.every(val => val === '' || val === null || val === undefined);
  }
  
  // For parent type filters - they don't have selected values themselves
  if (filter.type === 'parent') {
    return true; // Parent filters are always considered empty as they're containers
  }
  
  // For boolean types - false is a valid value, so only null/undefined is empty
  if (filter.type === 'boolean') {
    return filter.selected === null || filter.selected === undefined;
  }
  
  // For number types
  if (filter.type === 'number') {
    return filter.selected === null || filter.selected === undefined || filter.selected === '';
  }
  
  // For date types - need exactly 2 dates or an operation
  if (filter.type === 'date') {
    // Check for operation-based dates (thisWeek, lastMonth, etc.)
    if (typeof filter.selected === 'string' && filter.selected.trim() !== '') {
      return false;
    }
    // Check for date range
    if (Array.isArray(filter.selected)) {
      return filter.selected.length !== 2 || filter.selected.some((date: any) => !date || date === '');
    }
    return true;
  }
  
  // For string types
  if (typeof filter.selected === 'string') {
    return filter.selected.trim() === '';
  }
  
  return false;
};

/**
 * Removes empty filters from an array of filters
 */
export const cleanEmptyFilters = (filters: ActiveFilter[]): ActiveFilter[] => {
  return filters.filter(filter => !isFilterEmpty(filter));
};

/**
 * Checks if a filter has a meaningful value for API calls
 */
export const isFilterValid = (filter: ActiveFilter): boolean => {
  // Change log filters are always valid for API calls
  if (isChangeLogFilter(filter)) {
    return true;
  }
  
  if (isFilterEmpty(filter)) return false;
  
  // Parent filters are containers, not valid for API calls
  if (filter.type === 'parent') return false;
  
  // Boolean filters are valid if they have a boolean value
  if (filter.type === 'boolean') {
    return typeof filter.selected === 'boolean';
  }
  
  // Number filters are valid if they have a numeric value
  if (filter.type === 'number') {
    return typeof filter.selected === 'number' || (typeof filter.selected === 'string' && !isNaN(Number(filter.selected)));
  }
  
  // Date filters are valid if they have either:
  // 1. An array of 2 valid dates
  // 2. A string operation (thisWeek, lastMonth, etc.)
  if (filter.type === 'date') {
    if (typeof filter.selected === 'string') {
      return filter.selected.trim() !== '';
    }
    if (Array.isArray(filter.selected) && filter.selected.length === 2) {
      return filter.selected.every(date => date && date !== '');
    }
    return false;
  }
  
  // Checkbox and fetchableCheckbox filters need non-empty arrays
  if (filter.type === 'checkbox' || filter.type === 'fetchableCheckbox') {
    return Array.isArray(filter.selected) && filter.selected.length > 0;
  }
  
  return true;
};

/**
 * Updates a filter array by either adding/updating a filter or removing it if it becomes empty
 * This is a safe way to update filters that automatically handles empty filter removal
 */
export const updateFilterArray = (
  filters: ActiveFilter[], 
  filterUpdate: ActiveFilter
): ActiveFilter[] => {
  const index = filters.findIndex(f => 
    f.name === filterUpdate.name && f.parent === filterUpdate.parent
  );
  
  // If the update would result in an empty filter, remove it entirely
  if (isFilterEmpty(filterUpdate)) {
    if (index !== -1) {
      // Remove the existing filter
      return filters.filter((_, i) => i !== index);
    }
    // If filter doesn't exist and update is empty, just return current filters
    return filters;
  }
  
  // If filter exists, update it
  if (index !== -1) {
    const updatedFilters = [...filters];
    updatedFilters[index] = { ...updatedFilters[index], ...filterUpdate };
    return cleanEmptyFilters(updatedFilters);
  }
  
  // Add new filter if it doesn't exist and isn't empty
  return cleanEmptyFilters([...filters, filterUpdate]);
};

export const doesCheckboxInFilterMatch = (
  selected: ActiveFilter['selected'],
  value: unknown,
  options?: { ideaId?: string }
): boolean => {
  const selectedValues = Array.isArray(selected) ? selected.map(String) : [];

  if (selectedValues.includes('not_set') || selectedValues.includes('unassigned')) {
    return value == null || value === undefined;
  }

  if (selectedValues.includes('set')) {
    return value != null && value !== undefined;
  }

  if (selectedValues.includes('thisIdea') && options?.ideaId) {
    return String(value) === String(options.ideaId);
  }

  if (value == null || value === '') {
    return false;
  }

  const normalizedValue = String(value).toLowerCase();
  return selectedValues.some(
    (selectedValue) => selectedValue.toLowerCase() === normalizedValue
  );
};

export const isThemeEvidenceIdFilter = (filter: {
  name?: string;
  filterObject?: string;
}): boolean => filter?.name === '_id' && filter?.filterObject === 'evidence';

export const evidenceIdSetsEqual = (left: unknown[], right: unknown[]): boolean => {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right.map((id) => String(id)));
  return left.every((id) => rightSet.has(String(id)));
};

export const themeMatchesEvidenceIdFilter = (
  themeEvidenceIds: string[] | undefined,
  filter: {
    name?: string;
    filterObject?: string;
    selected?: unknown;
    label?: string;
  },
  themeTitle?: string
): boolean => {
  if (!isThemeEvidenceIdFilter(filter)) return false;
  if (themeTitle && filter.label && filter.label === themeTitle) return true;
  if (
    Array.isArray(filter.selected) &&
    filter.selected.length > 0 &&
    themeEvidenceIds?.length
  ) {
    return evidenceIdSetsEqual(filter.selected, themeEvidenceIds);
  }
  return false;
};

export const isThemeTitleDeepLinked = (
  themeTitle: string | undefined,
  deepLinkedTitle: string | null
): boolean => {
  if (!themeTitle || !deepLinkedTitle) return false;
  return themeTitle.toLowerCase() === deepLinkedTitle.toLowerCase();
};
